import { useRef, useState } from "react";
import type { FormEvent } from "react";
import { ApiError, API_BASE, api } from "@/lib/apiClient";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";

interface ImportResult {
  imported?: number;
  skipped?: number;
  failed?: number;
  errors?: Array<{ row?: number; reason?: string; [key: string]: unknown }>;
  [key: string]: unknown;
}

export function ProjectImportSection() {
  const importFileRef = useRef<HTMLInputElement>(null);
  const resolveFileRef = useRef<HTMLInputElement>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);

  const [resolveResult, setResolveResult] = useState<ImportResult | null>(null);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);

  function downloadTemplate() {
    const url = `${API_BASE || ""}/admin/import-template`;
    window.open(url, "_blank", "noopener");
  }

  async function uploadImport(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const file = importFileRef.current?.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setImporting(true);
    setImportError(null);
    setImportResult(null);
    try {
      const result = await api.postForm<ImportResult>("/admin/import-projects", fd);
      setImportResult(result);
      if (importFileRef.current) importFileRef.current.value = "";
    } catch (err) {
      setImportError(
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Import fehlgeschlagen.",
      );
    } finally {
      setImporting(false);
    }
  }

  async function uploadResolve(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const file = resolveFileRef.current?.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setResolving(true);
    setResolveError(null);
    setResolveResult(null);
    try {
      const result = await api.postForm<ImportResult>("/admin/import-projects-resolve", fd);
      setResolveResult(result);
      if (resolveFileRef.current) resolveFileRef.current.value = "";
    } catch (err) {
      setResolveError(
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Auflösen fehlgeschlagen.",
      );
    } finally {
      setResolving(false);
    }
  }

  return (
    <Card
      title="Projekte importieren"
      description="XLSX-Datei hochladen, mit Hero-Kontakten und Objektadressen abgleichen lassen."
      actions={
        <Button variant="secondary" onClick={downloadTemplate}>
          XLSX-Vorlage
        </Button>
      }
    >
      <div className="grid gap-6 md:grid-cols-2">
        <form onSubmit={uploadImport} className="space-y-3">
          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200">
            Datei importieren
          </h3>
          <input
            ref={importFileRef}
            type="file"
            accept=".xlsx"
            required
            className="block w-full text-sm text-slate-700 file:mr-3 file:rounded file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-sm file:font-medium hover:file:bg-slate-200 dark:text-slate-200 dark:file:bg-slate-800 dark:file:text-slate-100 dark:hover:file:bg-slate-700"
          />
          <Button type="submit" loading={importing}>
            Import starten
          </Button>
          {importError && (
            <Alert variant="error" title="Fehler">
              {importError}
            </Alert>
          )}
          {importResult && <ResultBlock result={importResult} />}
        </form>

        <form onSubmit={uploadResolve} className="space-y-3">
          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200">
            Fehler auflösen
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Mapping nutzt Hero-Kontakte und Objektadressen, um IDs nachzuziehen.
          </p>
          <input
            ref={resolveFileRef}
            type="file"
            accept=".xlsx"
            required
            className="block w-full text-sm text-slate-700 file:mr-3 file:rounded file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-sm file:font-medium hover:file:bg-slate-200 dark:text-slate-200 dark:file:bg-slate-800 dark:file:text-slate-100 dark:hover:file:bg-slate-700"
          />
          <Button type="submit" variant="secondary" loading={resolving}>
            IDs auflösen
          </Button>
          {resolveError && (
            <Alert variant="error" title="Fehler">
              {resolveError}
            </Alert>
          )}
          {resolveResult && <ResultBlock result={resolveResult} />}
        </form>
      </div>
    </Card>
  );
}

function ResultBlock({ result }: { result: ImportResult }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200">
      <pre className="whitespace-pre-wrap break-words">{JSON.stringify(result, null, 2)}</pre>
    </div>
  );
}
